import { ReactElement, useEffect, useRef, useState } from 'react';
import { Input, ActionIcon, Popover } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Component as SearchIcon } from 'icons/icon-ui--search.svg';
import { BreakpointMedia } from '../../utils/config';
import styles from './HeaderSearch.module.scss';

const STORAGE_KEY = 'recentSearches';
const MAX_RECENT = 5;

function getRecentSearches(): string[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function saveSearch(query: string) {
  const trimmed = query.trim();
  if (!trimmed) return;
  const existing = getRecentSearches()
    .filter((s) => s !== trimmed);
  localStorage.setItem(STORAGE_KEY, JSON.stringify([trimmed, ...existing].slice(0, MAX_RECENT)));
}

type Props = {
  autoFocus?: boolean;
  onBlur?: () => void;
  isLoggedIn?: boolean;
};

export function HeaderSearch({
  autoFocus = false,
  onBlur,
  isLoggedIn = false,
}: Props): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const initialQuery = (searchParams.get('search') ?? '').replace(/^'|'$/g, '');
  const [value, setValue] = useState(initialQuery);
  const [isFocused, setIsFocused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!!initialQuery || autoFocus);
  const isMobile = useMediaQuery(BreakpointMedia.MAX_LG);
  const recentSearches = getRecentSearches();
  const showRecent = isFocused && recentSearches.length > 0 && value === '';
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = (params.get('search') ?? '').replace(/^'|'$/g, '');
    setValue(q);
    if (q) setIsExpanded(true);
  }, [location.search]);

  const serializeParams = (params: URLSearchParams) => params.toString()
    .replace(/%2C/gi, ',')
    .replace(/%27/gi, "'");

  const handleSearch = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    saveSearch(trimmed);
    if (window.heap) window.heap.track('Search Submitted', { query: trimmed.toLowerCase() });
    const base = isLoggedIn ? '/upcoming-menus' : '/shop';
    const params = new URLSearchParams(searchParams);
    params.set('search', `'${trimmed}'`);
    sessionStorage.setItem('isUserInitiatedSearch', 'true');
    navigate(`${base}?${serializeParams(params)}${location.hash}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(value);
      inputRef.current?.blur();
    }
  };

  const recentItems = recentSearches.map((search) => (
    <li key={search}>
      <button
        type="button"
        className={styles.recent_item}
        onMouseDown={() => {
          setValue(search);
          handleSearch(search);
        }}
      >
        <SearchIcon width={14} height={14} />
        {search}
      </button>
    </li>
  ));

  // Always show input on desktop; on mobile only when expanded (autoFocus seeds this on mount)
  const showInput = isMobile !== true || isExpanded;

  const handleExpandOrSearch = () => {
    if (!isMobile && !isExpanded) {
      setIsExpanded(true);
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      handleSearch(value);
    }
  };

  return (
    <div className={styles.search_wrapper}>
      {!showInput && (
        <ActionIcon
          variant="transparent"
          size="sm"
          onClick={handleExpandOrSearch}
          aria-label="Search"
          className={styles.search_icon_button}
        >
          <SearchIcon width={18} height={18} />
        </ActionIcon>
      )}

      {showInput && (
        <Popover
          opened={!isMobile && showRecent}
          width="target"
          position="bottom-start"
          withinPortal
          shadow="md"
          radius="md"
          styles={{ dropdown: { padding: '4px 0' } }}
        >
          <Popover.Target>
            <Input
              ref={inputRef}
              icon={(
                <ActionIcon
                  variant="transparent"
                  size="sm"
                  onClick={handleExpandOrSearch}
                  aria-label="Search"
                >
                  <SearchIcon width={16} height={16} />
                </ActionIcon>
              )}
              styles={{
                icon: {
                  paddingLeft: 8,
                  cursor: 'pointer',
                },
              }}
              placeholder="Search recipes, ingredients, cuisines..."
              value={value}
              onChange={(e) => setValue(e.currentTarget.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setTimeout(() => {
                setIsFocused(false);
                if (!isMobile && !value) setIsExpanded(false);
                if (window.heap) window.heap.track('Search Bar Lose Focus', { query: value });
                onBlur?.();
              }, 150)}
              rightSection={
                isFocused ? (
                  <ActionIcon
                    size="xs"
                    variant="transparent"
                    className={styles.clear_button}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setValue('');
                      inputRef.current?.blur();
                      const base = isLoggedIn ? '/upcoming-menus' : '/shop';
                      const params = new URLSearchParams(searchParams);
                      params.delete('search');
                      const qs = serializeParams(params);
                      sessionStorage.setItem('isUserInitiatedSearch', 'true');
                      navigate(qs ? `${base}?${qs}${location.hash}` : `${base}${location.hash}`);
                    }}
                  >
                    ✕
                  </ActionIcon>
                ) : undefined
              }
              size="sm"
            />
          </Popover.Target>
          <Popover.Dropdown>
            <ul className={styles.recent_list}>{recentItems}</ul>
          </Popover.Dropdown>
        </Popover>
      )}

      {isMobile && showRecent && (
        <div className={styles.mobile_recent_dropdown}>
          <ul className={styles.recent_list}>{recentItems}</ul>
        </div>
      )}

      {isMobile && isFocused && (
        <div className={styles.search_overlay} />
      )}
    </div>
  );
}
